import React, { useState, useEffect, useRef } from "react";
import {
    Container,
    Row,
    Col,
    Form,
    Button,
    Tabs,
    Tab,
    Table,
} from "react-bootstrap";
import { parseCurl, parseNestedStrings, syntaxHighlight } from "../../Helpers/utils";
import { Notify } from "../../Helpers/notification";
import "./ApiBuilder.css"
import { PROXY_URL } from "../../Helpers/constant";

const ApiBuilder = ({
    initialUrl = "",
    initialMethod = "GET",
    initialHeaders = [{ key: "", value: "" }],
    initialBody = [{ key: "", value: "" }],
    onUseUrl,
    onMethod,
    onResponse,
    onStateChange,
}) => {
    const [url, setUrl] = useState(initialUrl);
    const [headers, setHeaders] = useState(initialHeaders);
    const [body, setBody] = useState(initialBody);
    const [rawHeaders, setRawHeaders] = useState("");
    const [rawBody, setRawBody] = useState("");
    const [headerMode, setHeaderMode] = useState("key-value"); 
    const [bodyMode, setBodyMode] = useState("key-value"); 
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const controllerRef = useRef(null);

    useEffect(() => {
        if (onStateChange) {
            onStateChange({ url, method:initialMethod, headers, body, rawBody, bodyMode, headerMode, rawHeaders });
        }
    }, [url, initialMethod, headers, body, rawBody, bodyMode, headerMode, rawHeaders, onStateChange]);

    const handleAddHeader = () => {
        const last = headers[headers.length - 1];
        if (!last.key && !last.value) return;
        setHeaders([...headers, { key: "", value: "" }]);
    };

    const handleHeaderChange = (i, field, value) => {
        const updated = [...headers];
        updated[i][field] = value;
        setHeaders(updated);
    };

    const removeHeader = (index) => {
        const updated = headers.filter((_, i) => i !== index);
        setHeaders(updated.length ? updated : [{ key: "", value: "" }]);
    };

    const handleHeaderModeChange = (mode) => {
        if (mode === "raw") {
            const obj = headers.reduce((acc, { key, value }) => {
                if (key) acc[key] = value;
                return acc;
            }, {});
            setRawHeaders(Object.keys(obj).length ? JSON.stringify(obj, null, 2) : "");
            setHeaderMode("raw");
        } else {
            try {
                const parsed = JSON.parse(rawHeaders || "{}");
                const arr = Object.entries(parsed).map(([key, val]) => ({
                    key,
                    value: typeof val === "string" ? val : JSON.stringify(val),
                }));
                setHeaders(arr.length ? arr : [{ key: "", value: "" }]);
                setHeaderMode("key-value");
            } catch {
                Notify.notify("Invalid JSON in raw headers");
            }
        }
    };

    const handleAddBody = () => {
        const last = body[body.length - 1];
        if (!last.key && !last.value) return;
        setBody([...body, { key: "", value: "" }]);
    };

    const handleBodyChange = (i, field, value) => {
        const updated = [...body];
        updated[i][field] = value;
        setBody(updated);
    };

    const handleUrlChange = (e) => {
        const value = e.target.value;
        setUrl(value);

        if (value.trim().startsWith("curl")) {
            handleClear();
            const parsed = parseCurl(value);
            setUrl(parsed.url ?? "");
            if (parsed.method) {
                onMethod(parsed.method);
            }
            const headerArray = Object.entries(parsed.headers).map(([key, value]) => ({
                key,
                value,
            }));
            setHeaders(headerArray.length ? headerArray : [{ key: "", value: "" }]);

            if (parsed.body) {
                try {
                    const bodyObj = JSON.parse(parsed.body);
                    const bodyArray = Object.entries(bodyObj).map(([key, value]) => ({
                        key,
                        value: (value && typeof value === "object" && !Array.isArray(value))
                            ? JSON.stringify(value)
                            : value
                    }));
                    setBody(bodyArray.length ? bodyArray : [{ key: "", value: "" }]);
                    setBodyMode("key-value");
                } catch {
                    const params = new URLSearchParams(parsed.body);
                    const bodyArray = [];
                    params.forEach((value, key) => {
                        bodyArray.push({ key, value });
                    });
                    setBody(bodyArray.length ? bodyArray : [{ key: "", value: "" }]);
                    setBodyMode("key-value");
                }
            } else {
                setBody([{ key: "", value: "" }]);
            }
        }
    };

    const sendRequest = async () => {
        if (!url) {
            Notify.notify("Please enter a URL");
            return null;
        }
        setLoading(true);
        const abortController = new AbortController();
        controllerRef.current = abortController;

        let headerObj = {};

        if (headerMode === "raw") {
            try {
                headerObj = JSON.parse(rawHeaders || "{}");
            } catch {
                Notify.notify("Raw headers must be valid JSON");
                setLoading(false);
                return;
            }
        } else {
            headerObj = headers.reduce((acc, { key, value }) => {
                if (key) acc[key] = value;
                return acc;
            }, {});
        }

        let requestBody = null;
        if (initialMethod === "POST") {
            const contentTypeKey = Object.keys(headerObj).find(k => k.toLowerCase() === "content-type");
            const isJson = contentTypeKey ? headerObj[contentTypeKey].includes("application/json") : false;

            if (bodyMode === "raw") {
                requestBody = rawBody; 
            } else if (isJson) {
                const bodyObj = body.reduce((acc, { key, value }) => {
                    if (!key) return acc;
                    acc[key] = parseNestedStrings(value);
                    return acc;
                }, {});
                requestBody = JSON.stringify(bodyObj);
            } else {
                const formData = new URLSearchParams();
                body.forEach(({ key, value }) => {
                    if (key) formData.append(key, value);
                });
                requestBody = formData.toString();
                
                if (!contentTypeKey) {
                    headerObj["Content-Type"] = "application/x-www-form-urlencoded";
                }
            }
        }

        try {
            const res = await fetch(PROXY_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    url,
                    method:initialMethod, 
                    headers: JSON.stringify(headerObj),
                    body: requestBody
                }),
                signal: abortController.signal,
            });

            const contentType = res.headers.get("content-type");
            const data =
                contentType && contentType.includes("application/json")
                    ? await res.json()
                    : await res.text();

            const responseObj = {
                status: data.status,
                statusText: data.statusText,
                headers: data.headers,
                data:data.data,
            };

            setResponse(responseObj);

            return responseObj;
        } catch (err) {
            if (err.name === "AbortError") Notify.notify("Request aborted!");
            else Notify.notify(err.message);
            return null;
        } finally {
            setLoading(false);
            controllerRef.current = null;
        }
    };

    const stopRequest = () => {
        if (controllerRef.current) {
            controllerRef.current.abort();
            controllerRef.current = null;
        }
    };

    const removeBody = (index) => {
        const updated = body.filter((_, i) => i !== index);
        setBody(updated.length ? updated : [{ key: "", value: "" }]);
    };

    const handleClear = () => {
        setUrl("");
        setHeaders([{ key: "", value: "" }]);
        setBody([{ key: "", value: "" }]);
        setRawBody("");
        setBodyMode("key-value");
        setResponse(null);
    };

    const handleSubmit = async () => {
        if (!url) return;
        if (onUseUrl) onUseUrl(url);
        if (onResponse) {
            const bodyString =
                bodyMode === "raw"
                    ? rawBody
                    : JSON.stringify(
                        body.reduce((acc, { key, value }) => {
                            if (!key) return acc;
                            acc[key] = parseNestedStrings(value);
                            return acc;
                        }, {}),
                        null,
                        2
                    );
            onResponse(bodyString);
        }

    };

    const handleBodyModeChange = (mode) => {
        if (mode === "raw") {
            const obj = body.reduce((acc, { key, value }) => {
                if (!key) return acc;

                acc[key] = parseNestedStrings(value);

                return acc;
            }, {});
            setRawBody(Object.keys(obj).length ? JSON.stringify(obj, null, 2) : "");
            setBodyMode("raw");
        } else if (mode === "key-value") {
            try {
                const parsed = JSON.parse(rawBody || "{}");
                const arr = Object.entries(parsed).map(([key, val]) => ({
                    key,
                    value: typeof val === "string" ? val : JSON.stringify(val),
                }));
                setBody(arr.length ? arr : [{ key: "", value: "" }]);
                setBodyMode("key-value");
            } catch (err) {
                Notify.notify("Invalid JSON in raw body");
            }
        }
    };

    return (
        <Container className="mt-4 api-tester">
            <h4 className="mb-4 fw-bold border-bottom pb-2">API Builder</h4>
            <Row className="align-items-center mb-3">
                <Col md={10} className="w-100">
                    <Form.Label className="fw-semibold">Request URL</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Enter URL or paste full cURL here"
                        value={url}
                        size="lg"
                        onChange={handleUrlChange}
                        style={{ fontSize: "1rem" }}
                    />
                </Col>
            </Row>

            <Tabs defaultActiveKey="headers" className="mb-3 custom-tabs" fill>
                <Tab eventKey="headers" title="Headers">
                    <div className="tab-section">
                       <div className="mb-2">
                            <Button
                                variant={headerMode === "key-value" ? "primary" : "outline-primary"}
                                size="sm"
                                className="me-2"
                                onClick={() => handleHeaderModeChange("key-value")}
                            >
                                Key-Value
                            </Button>

                            <Button
                                variant={headerMode === "raw" ? "primary" : "outline-primary"}
                                size="sm"
                                onClick={() => handleHeaderModeChange("raw")}
                            >
                                Raw
                            </Button>
                        </div>

                        {headerMode === "key-value" ? (
                            <>
                                {headers.map((h, i) => (
                                    <Row key={i} className="mb-2">
                                        <Col md={5}>
                                            <Form.Control
                                                placeholder="Key"
                                                value={h.key}
                                                onChange={(e) =>
                                                    handleHeaderChange(i, "key", e.target.value)
                                                }
                                            />
                                        </Col>
                                        <Col md={5}>
                                            <Form.Control
                                                placeholder="Value"
                                                value={h.value}
                                                onChange={(e) =>
                                                    handleHeaderChange(i, "value", e.target.value)
                                                }
                                            />
                                        </Col>
                                        <Col md={2} className="text-center">
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => removeHeader(i)}
                                            >
                                                −
                                            </Button>
                                        </Col>
                                    </Row>
                                ))}
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleAddHeader}
                                >
                                    + Add Header
                                </Button>
                            </>
                        ) : (
                            <Form.Control
                                as="textarea"
                                rows={8}
                                placeholder="Enter raw JSON headers"
                                value={rawHeaders}
                                onChange={(e) => setRawHeaders(e.target.value)}
                            />
                        )}
                    </div>
                </Tab>

                <Tab eventKey="body" title="Body">
                    <div className="tab-section">
                        <div className="mb-2">
                            <Button 
                                variant={bodyMode === "key-value" ? "primary" : "outline-primary"} 
                                size="sm" 
                                onClick={() => handleBodyModeChange("key-value")}
                                className="me-2"
                            >
                                Key-Value
                            </Button>
                            <Button 
                                variant={bodyMode === "raw" ? "primary" : "outline-primary"} 
                                size="sm" 
                                onClick={() => handleBodyModeChange("raw")}
                            >
                                Raw
                            </Button>
                        </div>

                        {bodyMode === "key-value" ? (
                            <div>
                                {body.map((b, i) => (
                                    <Row key={i} className="mb-2">
                                        <Col md={5}>
                                            <Form.Control
                                                placeholder="Key"
                                                value={b.key}
                                                onChange={(e) =>
                                                    handleBodyChange(i, "key", e.target.value)
                                                }
                                            />
                                        </Col>
                                        <Col md={5}>
                                            <Form.Control
                                                placeholder="Value"
                                                value={b.value}
                                                onChange={(e) =>
                                                    handleBodyChange(i, "value", e.target.value)
                                                }
                                            />
                                        </Col>
                                        <Col md={2} className="text-center">
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => removeBody(i)}
                                            >
                                                −
                                            </Button>
                                        </Col>
                                    </Row>
                                ))}
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleAddBody}
                                >
                                    + Add Body Parameter
                                </Button>
                            </div>
                        ) : (
                            <Form.Group>
                                <Form.Control
                                    as="textarea"
                                    rows={8}
                                    placeholder='Enter raw JSON body here'
                                    value={rawBody}
                                    onChange={(e) => setRawBody(e.target.value)}
                                />
                            </Form.Group>
                        )}
                    </div>
                </Tab>
            </Tabs>

            <div className="mt-3">
                {loading ? (
                    <Button variant="danger" onClick={stopRequest}>
                        Stop
                    </Button>
                ) : (
                    <>
                        <Button onClick={sendRequest} disabled={loading} variant="primary">
                            Test
                        </Button>
                        {url && (
                            <Button
                                variant="success"
                                className="ms-2"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                Submit
                            </Button>
                        )}
                        <Button
                            variant="outline-secondary"
                            className="ms-2"
                            onClick={handleClear}
                        >
                            Clear
                        </Button>
                    </>
                )}
            </div>

            {response && (
                <div className="mt-4 response-section">
                    <h5 className="fw-bold border-bottom pb-2">Response</h5>
                    <p>
                        <strong>Status:</strong> {response.status} {response.statusText}
                    </p>
                    <Tabs defaultActiveKey="data" className="mb-3 custom-tabs">
                        <Tab eventKey="data" title="Data">
                            <pre
                                className="p-3 rounded border json-pre"
                                dangerouslySetInnerHTML={{
                                    __html: (() => {
                                        try {
                                            const jsonData =
                                                typeof response.data === "object"
                                                    ? response.data
                                                    : JSON.parse(response.data);
                                            return syntaxHighlight(jsonData);
                                        } catch {
                                            return response.data;
                                        }
                                    })(),
                                }}
                            ></pre>
                        </Tab>
                        <Tab eventKey="headers" title="Headers">
                            <Table striped bordered hover size="sm">
                                <tbody>
                                    {Object.entries(response.headers).map(([k, v]) => (
                                        <tr key={k}>
                                            <td>{k}</td>
                                            <td>{v}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Tab>
                    </Tabs>
                </div>
            )}
        </Container>
    );
};

export default ApiBuilder;
